import { appConfig } from '../config/app-config';
import type { BrandConfig, BrandConfigResponse } from '../types/brand.types';
import { logError } from './rollbar';
import fallbackBrandConfig from '../config/fallback-brand-config.json';

/**
 * Loads brand configuration from local bundled config file
 * The config file is downloaded from CDN during build time
 * Falls back to brand "0004" if the specified brand is not found
 */
export async function loadBrandConfig(brandId: string = '0004'): Promise<BrandConfig> {
  try {
    console.log('📦 Loading brand config from local bundle');

    const data = fallbackBrandConfig as BrandConfigResponse;

    // Try to get the specified brand, fall back to "0004"
    let brandData = data[brandId];

    if (!brandData || typeof brandData === 'string') {
      console.warn(`Brand "${brandId}" not found, falling back to default brand "0004"`);
      brandData = data['0004'];

      if (!brandData || typeof brandData === 'string') {
        throw new Error('Default brand "0004" not found in config');
      }
    }

    // Handle nested branding structure from the example JSON
    // The config has structure: { "0000": { "branding": { ... } } }
    if ('branding' in brandData) {
      return (brandData as any).branding;
    }

    return brandData as BrandConfig;
  } catch (error) {
    logError('Error loading brand config from local bundle:', error);
    // Return a minimal default configuration
    console.warn('⚠️  Using minimal default brand config');
    return {
      primaryColor: '#022449',
      secondaryColor: '#005595',
      tertiaryColor: '#f3f3f3',
      images: {
        favicon: '',
        logo: ''
      }
    };
  }
}

/**
 * Gets the full URL for a brand image
 */
export function getBrandImageUrl(imageName: string): string {
  if (!imageName) return '';

  const brandBasePath = appConfig.r2path.replace(/\/+$/, '');

  return `${brandBasePath}/brand/${imageName}`;
}
